import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Stack,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Autocomplete,
  IconButton, // Import Pagination
} from "@mui/material";
import {
  Star,
  Email,
  CalendarToday,
  Phone,
  Visibility,
  Search,
  Sort,
  Badge,
  Public,
  Refresh,
} from "@mui/icons-material";
import { useAuth } from "context/authContext";

// Replace with your actual imports
import MDButton from "components/MDButton";
import LoadingScreen from "components/Loading";
import ApplicantDialog from "components/ApplicantDialog";
import {
  getApplicantDataByForm,
  getMeetingsByForm,
  getAllMeetings,
  getApplicantsPageData,
} from "services/api";
import { fieldStyles } from "services/utils";
// import { getApplicantDataByForm } from ...

const ITEMS_PER_PAGE = 15; // Limit set to 20

const ApplicantsPage = ({ form, formQuestions, canEdit, empRequestedBy }) => {
  console.log("Form::", form);
  const { t, i18n } = useTranslation();
  const [refresh, setRefresh] = useState(true);
  const { employee } = useAuth();
  // Define options with translation mapping
  const statuses = [
    {
      value: "Pending Review",
      label: t("admin.applicants.filters.status_pending"),
      color: "info", // Blue
    },
    {
      value: "Shortlisted",
      label: t("admin.applicants.filters.status_shortlisted"),
      color: "warning", // Orange
    },
    {
      value: "Accepted",
      label: t("admin.applicants.filters.status_accepted"),
      color: "success", // Green
    },
    {
      value: "Rejected",
      label: t("admin.applicants.filters.status_rejected"),
      color: "error", // Red
    },
    {
      value: "No Show",
      label: t("admin.applicants.filters.status_no_show"),
      color: "error", // Red (Negative outcome)
    },
    {
      value: "Excused",
      label: t("admin.applicants.filters.status_excused"),
      color: "info", // Purple/Grey
    },
    {
      value: "Rescheduled",
      label: t("admin.applicants.filters.status_rescheduled"),
      color: "warning", // Light Blue
    },
    {
      value: "Cancelled",
      label: t("admin.applicants.filters.status_cancelled"),
      color: "info", // Grey
    },
    {
      value: "Pipeline",
      label: t("admin.applicants.filters.status_pipeline"),
      color: "info", // Main Theme Color (Active status)
    },
    {
      value: "Position Closed",
      label: t("admin.applicants.filters.status_position_closed"),
      color: "error", // Grey
    },
  ];

  const getStatusColor = (status) => {
    const found = statuses.find((s) => s.value === status);
    return found ? found.color : "default";
  };

  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  // Filters & Sorting State
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState("All");

  // Pagination State
  const [page, setPage] = useState(1);

  const [meetings, setMeetings] = useState({});
  const [allMeetings, setAllMeetings] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      // const applicantsPageData = await getApplicantsPageData(form.id);
      try {
        setLoading(true);

        const data = await getApplicantDataByForm(form.id);
        console.log("Data::", data);
        if (isMounted && data !== "error") {
          setApplicants(data || []);
        }

        const meetingsData = await getMeetingsByForm(form.id);
        if (isMounted && meetingsData !== "error") {
          setMeetings(meetingsData || {});
        }

        const allMeetings = await getAllMeetings();
        if (allMeetings !== "error") {
          setAllMeetings(allMeetings);
        }
      } catch (error) {
        console.error("Failed to load applicants", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (form?.id) {
      fetchData();
    }
  }, [form, refresh]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // 1. Filter and Sort Logic
  const processedApplicants = useMemo(() => {
    let result = [...applicants];

    // A. Filter by Name
    if (searchTerm.trim()) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter((app) => app.fullName.toLowerCase().includes(lowerTerm));
    }

    // B. Filter by Status (Multi-select)
    if (statusFilter.length > 0) {
      result = result.filter((app) => statusFilter.includes(app.status));
    }

    // C. NEW: Filter by Type (Internal/External)
    if (typeFilter === "Internal") {
      // Internal applicants have an applicantId (not null)
      // result = result.filter((app) => app.applicantId != null);
      result = result.filter((app) => !String(app.phone || "").trim());
    } else if (typeFilter === "External") {
      // External applicants have null applicantId
      // result = result.filter((app) => app.applicantId == null);
      result = result.filter((app) => String(app.phone || "").trim().length > 0);
    }

    // D. Sort by Rating
    result.sort((a, b) => {
      const ratingA = a.rating || 0;
      const ratingB = b.rating || 0;

      return sortOrder === "desc" ? ratingB - ratingA : ratingA - ratingB;
    });

    return result;
  }, [applicants, searchTerm, sortOrder, statusFilter, typeFilter]);

  // 2. Pagination Logic: Slice the processed list
  const paginatedApplicants = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return processedApplicants.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [processedApplicants, page]);

  // Handle Page Change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Optional: Scroll to top of grid when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOpenDialog = (applicant) => {
    setSelectedApplicant(applicant);
  };

  const handleCloseDialog = () => {
    setSelectedApplicant(null);
  };

  const handleStatusUpdate = (id, newStatus) => {
    setApplicants((prev) =>
      prev.map((app) => (app.id === id ? { ...app, status: newStatus } : app))
    );
    // Also update the selectedApplicant so the dialog UI updates instantly
    if (selectedApplicant && selectedApplicant.id === id) {
      setSelectedApplicant((prev) => ({ ...prev, status: newStatus }));
    }
  };

  const totalFiltered = processedApplicants.length;
  const start = totalFiltered === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;
  const end = Math.min(page * ITEMS_PER_PAGE, totalFiltered);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      {/* Page Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t("admin.applicants.app_form")}
          </Typography>
          <Typography variant="button" color="text.secondary" fontWeight="regular">
            {t("admin.applicants.meta.showing_stats", {
              start: start,
              end: end,
              total: totalFiltered,
            })}
          </Typography>
        </Box>
        <Box>
          <Chip
            label={t("admin.applicants.meta.total_chip", {
              count: applicants.length,
            })}
            color="primary"
            variant="outlined"
          />
          <Tooltip title={t("common.refresh") || "Refresh"}>
            <IconButton
              onClick={() => setRefresh(!refresh)}
              color="secondary" // or "primary" or "info"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter & Sort Toolbar */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* 1. Search Box (Adjusted width to 4) */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t("admin.applicants.filters.search_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* 2. Status Filter (Adjusted width to 3) */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              id="status-filter"
              options={statuses}
              disableCloseOnSelect
              limitTags={1}
              getOptionLabel={(option) => option.label}
              value={statuses.filter((opt) => statusFilter.includes(opt.value))}
              onChange={(event, newValue) => setStatusFilter(newValue.map((v) => v.value))}
              ChipProps={{
                size: "small",
                sx: { maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" },
              }}
              renderOption={(props, option, { selected }) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...props}>
                  <Box
                    component="span"
                    sx={{
                      color: (theme) => theme.palette[option.color].main,
                      fontWeight: selected ? "bold" : "normal",
                    }}
                  >
                    {option.label}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label={t("admin.applicants.filters.status_label")}
                  placeholder={
                    statusFilter.length === 0 ? t("admin.applicants.filters.status_all") : ""
                  }
                />
              )}
            />
          </Grid>

          {/* 3. NEW: Applicant Type Filter (Width 3) */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("admin.applicants.filters.type_label")}</InputLabel>
              <Select
                value={typeFilter}
                label={t("admin.applicants.filters.type_label")}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={fieldStyles}
              >
                <MenuItem value="All">
                  <em>{t("admin.applicants.filters.type_all")}</em>
                </MenuItem>
                <MenuItem value="Internal">{t("admin.applicants.filters.type_internal")}</MenuItem>
                <MenuItem value="External">{t("admin.applicants.filters.type_external")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* 4. Sort Dropdown (Adjusted width to 2) */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("admin.applicants.filters.sort_label")}</InputLabel>
              <Select
                value={sortOrder}
                label={t("admin.applicants.filters.sort_label")}
                onChange={(e) => setSortOrder(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <Sort fontSize="small" />
                  </InputAdornment>
                }
                sx={fieldStyles}
              >
                <MenuItem value="desc">{t("admin.applicants.filters.sort_desc")}</MenuItem>
                <MenuItem value="asc">{t("admin.applicants.filters.sort_asc")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Grid of Applicant Cards */}
      <Grid container spacing={3}>
        {/* Iterate over PAGINATED applicants, not the whole list */}
        {paginatedApplicants.map((applicant) => (
          <Grid item xs={12} sm={6} md={4} key={applicant.id}>
            <Card
              elevation={2}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "0.3s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Header: Name + Rating */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    <Typography variant="h6" fontWeight="bold" noWrap title={applicant.fullName}>
                      {applicant.fullName}
                    </Typography>
                    <Chip
                      // label={applicant.status}
                      label={
                        t(`admin.talentAcquisition.statusInMeeting.${applicant.status}`) ||
                        applicant.status
                      }
                      color={getStatusColor(applicant.status)}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5, fontWeight: "bold" }}
                    />
                  </Box>
                  <Tooltip title={t("admin.applicants.card.rating_tooltip")}>
                    <Box
                      sx={{
                        bgcolor: "warning.light",
                        color: "warning.dark",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        fontWeight: "bold",
                        minWidth: "fit-content",
                      }}
                    >
                      <Star fontSize="small" sx={{ mr: 0.5 }} />
                      {applicant.rating || 0}
                    </Box>
                  </Tooltip>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Key Info */}
                <Stack spacing={1.5}>
                  <Box display="flex" alignItems="center">
                    {String(applicant.phone || "").trim() ? (
                      <Public fontSize="small" sx={{ color: "text.secondary", mr: 1.5 }} />
                    ) : (
                      <Badge fontSize="small" sx={{ color: "text.secondary", mr: 1.5 }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontWeight: "regular",
                      }}
                    >
                      {String(applicant.phone || "").trim()
                        ? t("admin.applicants.dialog.sections.external_applicant")
                        : t("admin.applicants.dialog.sections.internal_applicant")}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Email fontSize="small" sx={{ color: "text.secondary", mr: 1.5 }} />
                    <Typography variant="body2" noWrap title={applicant.email}>
                      {applicant.email}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <Phone fontSize="small" sx={{ color: "text.secondary", mr: 1.5 }} />
                    <Typography variant="body2" noWrap>
                      {applicant.phone || t("admin.applicants.card.no_phone")}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <CalendarToday fontSize="small" sx={{ color: "text.secondary", mr: 1.5 }} />
                    <Typography variant="body2">
                      {new Date(applicant.submittedAt).toLocaleDateString(i18n.language)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>

              <Box p={2} pt={0}>
                <MDButton
                  variant="outlined"
                  color="primary"
                  fullWidth
                  endIcon={<Visibility />}
                  onClick={() => handleOpenDialog(applicant)}
                >
                  {t("admin.applicants.card.view_analysis")}
                </MDButton>
              </Box>
            </Card>
          </Grid>
        ))}

        {processedApplicants.length === 0 && (
          <Grid item xs={12}>
            <Box textAlign="center" py={5} color="text.secondary">
              <Typography variant="h6">
                {searchTerm
                  ? t("admin.applicants.empty_states.no_match")
                  : t("admin.applicants.empty_states.no_data")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Pagination Control */}
      {processedApplicants.length > ITEMS_PER_PAGE && (
        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Pagination
            count={Math.ceil(processedApplicants.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            // MUI handles RTL automatically based on theme direction
          />
        </Box>
      )}

      {/* Detail Dialog */}
      <ApplicantDialog
        open={Boolean(selectedApplicant)}
        applicant={selectedApplicant}
        questions={formQuestions}
        onClose={handleCloseDialog}
        onStatusChange={handleStatusUpdate}
        canEdit={canEdit}
        meetings={meetings}
        employeeId={employee.id}
        allMeetings={allMeetings}
        empRequestedBy={empRequestedBy}
      />
    </Box>
  );
};

ApplicantsPage.propTypes = {
  form: PropTypes.object.isRequired,
  formQuestions: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
  empRequestedBy: PropTypes.string,
};

export default ApplicantsPage;
